/**
 * @fileoverview Centralized tooltip content mapping for all dashboard metrics.
 * Contains LaTeX formulas and explanations sourced from /docs/API_CONTEXT.md
 * and poissonCalculations.js logic.
 */

/**
 * Tooltip content mapping: metricKey -> {formula: LaTeX string, explanation: plain text}
 * Formulas use raw LaTeX syntax for KaTeX rendering (e.g., \\lambda for λ)
 */
export const tooltipContent = {
  // Time-based metrics
  timeSinceLastBlock: {
    formula: 't_{elapsed} = t_{current} - t_{last\\,block}',
    explanation: 'Time elapsed since the pool\'s most recent block was mined. Used as a reference point for probability calculations.'
  },
  expectedTime: {
    formula: 'E[T] = \\frac{1}{\\lambda}',
    explanation: 'Expected time to next block arrival. For the network, E[T] = 600 seconds (10 minutes). For a pool, E[T] depends on the pool\'s block rate λ.'
  },
  medianTime: {
    formula: 't_{median} = \\frac{\\ln(2)}{\\lambda} \\approx \\frac{0.693}{\\lambda}',
    explanation: 'Median time to next block (50th percentile). Half of all blocks will arrive before this time, half after. The median is shorter than the mean due to the exponential distribution\'s right skew.'
  },
  estimatedTimeLeft: {
    formula: 't_{remaining} = E[T] - t_{elapsed}',
    explanation: 'Estimated time remaining until the next block, calculated as expected time minus elapsed time. Can be negative if the pool is overdue (past expected time).'
  },
  
  // Probability metrics
  nextBlockProbabilities: {
    formula: 'P(T \\leq t | T > s) = \\frac{P(T \\leq t+s) - P(T \\leq s)}{1 - P(T \\leq s)}',
    explanation: 'Conditional probability of next block arriving within time window, given that time s has already elapsed. Uses the memoryless property of exponential distribution.'
  },
  probability1h: {
    formula: 'P(T \\leq 1h | T > t_{elapsed})',
    explanation: 'Probability that the next block will arrive within 1 hour, conditional on the time already elapsed since the last block.'
  },
  probability2h: {
    formula: 'P(T \\leq 2h | T > t_{elapsed})',
    explanation: 'Probability that the next block will arrive within 2 hours, conditional on the time already elapsed since the last block.'
  },
  probability4h: {
    formula: 'P(T \\leq 4h | T > t_{elapsed})',
    explanation: 'Probability that the next block will arrive within 4 hours, conditional on the time already elapsed since the last block.'
  },
  probability8h: {
    formula: 'P(T \\leq 8h | T > t_{elapsed})',
    explanation: 'Probability that the next block will arrive within 8 hours, conditional on the time already elapsed since the last block.'
  },
  
  // Percentiles
  percentile50: {
    formula: 't_{0.50} = \\frac{\\ln(2)}{\\lambda}',
    explanation: '50th percentile (median) time to next block. 50% of blocks will arrive before this time.'
  },
  percentile80: {
    formula: 't_{0.80} = \\frac{-\\ln(0.2)}{\\lambda} \\approx \\frac{1.609}{\\lambda}',
    explanation: '80th percentile time to next block. 80% of blocks will arrive before this time.'
  },
  percentile90: {
    formula: 't_{0.90} = \\frac{-\\ln(0.1)}{\\lambda} \\approx \\frac{2.303}{\\lambda}',
    explanation: '90th percentile time to next block. 90% of blocks will arrive before this time.'
  },
  percentile95: {
    formula: 't_{0.95} = \\frac{-\\ln(0.05)}{\\lambda} \\approx \\frac{2.996}{\\lambda}',
    explanation: '95th percentile time to next block. 95% of blocks will arrive before this time.'
  },
  confidenceIntervals: {
    formula: 'CI_{1-\\alpha} = [t_L, t_U] \\text{ where } t_L = \\frac{-\\ln(1-\\alpha/2)}{\\lambda}, t_U = \\frac{-\\ln(\\alpha/2)}{\\lambda}',
    explanation: 'Confidence interval for time to next block. For 90% confidence (α=0.1), the interval contains the true time with 90% probability.'
  },
  
  // Deviation and Z-score
  zScore: {
    formula: 'z = \\frac{observed - expected}{\\sigma} = \\frac{N_{obs} - \\lambda t}{\\sqrt{\\lambda t}}',
    explanation: 'Z-score measuring deviation from expected performance. |z| < 1 is normal, |z| < 2 is somewhat unusual (95% confidence), |z| > 2 is statistically significant.'
  },
  deviationBounds: {
    formula: 'bounds = E[T] \\pm z \\times \\sigma = E[T] \\pm z \\times E[T]',
    explanation: 'Deviation bounds around expected time, showing ±z standard deviations. For exponential distribution, standard deviation equals the mean (σ = E[T] = 1/λ).'
  },
  
  // Historical performance
  observedBlocks: {
    formula: 'N_{observed} = \\text{count of blocks in analysis window}',
    explanation: 'Actual number of blocks mined by the pool within the analysis time window (default: 24 hours).'
  },
  expectedBlocks: {
    formula: 'N_{expected} = \\lambda_{network} \\times t_{window} \\times \\text{hashrateShare}',
    explanation: 'Expected number of blocks based on pool\'s hashrate share. Calculated as network block rate × time window × pool share.'
  },
  growthFactor: {
    formula: 'growth\\_factor = \\frac{N_{observed}}{N_{expected}}',
    explanation: 'Historical growth factor comparing observed vs expected blocks. >1 means outperforming (lucky), <1 means underperforming (unlucky). Note: This is for historical analysis only and should not be used for predictions due to the memoryless property.'
  },
  
  // Backtesting metrics
  mae: {
    formula: 'MAE = \\frac{1}{n}\\sum_{i=1}^{n} |actual_i - predicted_i|',
    explanation: 'Mean Absolute Error: average absolute difference between predicted and actual block intervals. Lower is better. Measured in seconds.'
  },
  rmse: {
    formula: 'RMSE = \\sqrt{\\frac{1}{n}\\sum_{i=1}^{n}(actual_i - predicted_i)^2}',
    explanation: 'Root Mean Square Error: square root of average squared differences. Penalizes large errors more than MAE. Measured in seconds.'
  },
  calibration: {
    formula: 'calibration = \\frac{\\text{count}(actual_i < predicted_i)}{n} \\times 100\\%',
    explanation: 'Calibration percentage: proportion of actual intervals below the predicted median. For a well-calibrated exponential model, should be approximately 50%.'
  },
  
  // Pool and network metrics
  poolHashrate: {
    formula: 'H_{pool} = \\text{estimated hashrate from pool info}',
    explanation: 'Pool\'s estimated hashrate in H/s (hashes per second). Derived from recent block mining performance.'
  },
  networkShare: {
    formula: 'share = \\frac{H_{pool}}{H_{network}}',
    explanation: 'Pool\'s share of total network hashrate, expressed as a decimal (0-1) or percentage. Determines the pool\'s expected block rate.'
  },
  networkDifficulty: {
    formula: 'H_{network} \\approx \\frac{difficulty \\times 2^{32}}{600}',
    explanation: 'Network hashrate estimated from difficulty. The difficulty target is adjusted every 2016 blocks to maintain ~10 minute average block time.'
  },
  stableShare: {
    formula: 'stable\\_share = w_h \\times hashrateShare + w_b \\times blockShare',
    explanation: 'Weighted average of hashrate share and block share, smoothing volatility. Default weights: 60% hashrate, 40% block share. Used for calculating pool block rate λ.'
  },
  
  // Lambda calculations
  poolLambda: {
    formula: '\\lambda_{pool} = \\frac{H_{pool}}{H_{network}} \\times \\lambda_{network} = share \\times \\frac{1}{600}',
    explanation: 'Pool block rate (λ_pool): expected blocks per second for this pool. Calculated from pool\'s share of network hashrate multiplied by network block rate (1/600 blocks/second).'
  },
  
  // Distribution functions
  poissonPMF: {
    formula: 'P(N(t) = k) = \\frac{(\\lambda t)^k e^{-\\lambda t}}{k!}',
    explanation: 'Poisson Probability Mass Function: probability of exactly k blocks occurring in time interval t. Bitcoin block arrivals follow a Poisson process.'
  },
  exponentialPDF: {
    formula: 'f(t) = \\lambda e^{-\\lambda t}, \\quad t \\geq 0',
    explanation: 'Exponential Probability Density Function: probability density for time to next block. This is the curve shown in the probability distribution graph.'
  },
  exponentialCDF: {
    formula: 'P(T \\leq t) = 1 - e^{-\\lambda t}',
    explanation: 'Exponential Cumulative Distribution Function: probability that next block arrives within time t. This is the cumulative probability curve shown in the graph.'
  },
  
  // Block table metrics
  blockHeight: {
    formula: 'height = \\text{block height in blockchain}',
    explanation: 'Block height: sequential number of the block in the Bitcoin blockchain. Each block increments the height by 1.'
  },
  blockTimestamp: {
    formula: 'timestamp = \\text{Unix timestamp (seconds since epoch)}',
    explanation: 'Block timestamp: Unix timestamp when the block was mined, in seconds since January 1, 1970 UTC.'
  },
  blockReward: {
    formula: 'reward = subsidy + fees',
    explanation: 'Total block reward in BTC: sum of block subsidy (currently 3.125 BTC) plus all transaction fees collected in the block.'
  },
  medianFeeRate: {
    formula: 'medianFeeRate = \\text{median fee rate in sat/vB}',
    explanation: 'Median fee rate: middle value of all transaction fee rates in the block, measured in satoshis per virtual byte (sat/vB).'
  },
  avgFee: {
    formula: 'avgFee = \\frac{\\text{total fees}}{\\text{transaction count}}',
    explanation: 'Average fee per transaction: total transaction fees divided by number of transactions in the block, measured in satoshis.'
  },
  txCount: {
    formula: 'txCount = \\text{number of transactions in block}',
    explanation: 'Transaction count: total number of transactions included in the block (excluding the coinbase transaction).'
  },
  outlierIndicator: {
    formula: 'isOutlier = P(T \\leq t) < \\alpha/2 \\text{ or } P(T \\leq t) > 1-\\alpha/2',
    explanation: 'Outlier detection: blocks with inter-block intervals in the extreme tails (default: 5% tails, 2.5% each side) of the exponential distribution. Marked using iterative refinement with exponential CDF.'
  },
  showOutliers: {
    formula: 'filter: \\text{show/hide blocks with } isOutlier = true',
    explanation: 'Toggle to show or hide statistical outliers in the block table. Outliers are detected using exponential distribution CDF with iterative refinement.'
  },
  
  // Settings parameters
  analysisWindow: {
    formula: 'window = t_{window} \\text{ (default: 24 hours)}',
    explanation: 'Analysis window: time period used for historical performance metrics (observed blocks, expected blocks, growth factor, deviation). Does not affect prediction calculations which use all available blocks.'
  },
  stableShareWeights: {
    formula: 'weights: w_h + w_b = 1, \\text{ default } w_h=0.6, w_b=0.4',
    explanation: 'Stable share calculation weights: relative importance of hashrate share vs block share when computing stable share. Weights must sum to 1. Default: 60% hashrate, 40% block share.'
  },
  hashrateWeight: {
    formula: 'w_h = \\text{weight for hashrate share (0-1)}',
    explanation: 'Hashrate weight: weight assigned to hashrate share in stable share calculation. Higher values emphasize hashrate over recent block performance.'
  },
  blockShareWeight: {
    formula: 'w_b = 1 - w_h = \\text{weight for block share (0-1)}',
    explanation: 'Block share weight: weight assigned to block share in stable share calculation. Automatically set to 1 - hashrateWeight to ensure weights sum to 1.'
  },
  confidenceLevel: {
    formula: 'confidence = 1 - \\alpha \\text{ (default: 0.9 = 90\\%)}',
    explanation: 'Confidence level: probability that confidence intervals and deviation bands contain the true value. Default 0.9 means 90% confidence (α=0.1).'
  },
  outlierThreshold: {
    formula: 'pThreshold = \\alpha \\text{ (default: 0.05 = 5\\%)}',
    explanation: 'Outlier detection threshold: probability threshold for marking intervals as outliers. Default 0.05 means 5% of intervals are outliers (2.5% in each tail).'
  },
  maxIterations: {
    formula: 'maxIterations = \\text{max refinement iterations (default: 3)}',
    explanation: 'Maximum iterations for iterative outlier detection refinement. Algorithm refits lambda after removing outliers and repeats until convergence or max iterations reached.'
  },
  parameterOptimization: {
    formula: '\\min_{p, w_h} MAE(p, w_h) \\text{ via grid search}',
    explanation: 'Parameter optimization: automatically finds best outlier threshold (p) and hashrate weight (w_h) by minimizing Mean Absolute Error (MAE) from backtesting on historical data.'
  }
};

/**
 * Get tooltip content for a metric key
 * @param {string} metricKey - Key identifying the metric
 * @returns {{formula: string, explanation: string} | null} Tooltip content or null if key not found
 */
export function getTooltipContent(metricKey) {
  return tooltipContent[metricKey] || null;
}

